import { RefObject, useEffect, useRef } from "react";
// Types only: Chart.js is in the lazily loaded chart chunk
import type { Chart } from "chart.js";
import { useHoveredTrackpoint } from "stores/genericStore/GenericContext";
import { useRouting } from "stores/routingStore/RoutingContext";
import type { ChartDataPoint } from "./TrackChart";

/**
 * Shows the track point hovered on the map in the chart, as if the chart itself was hovered there:
 * the point is activated in Chart.js, whose tooltip callback then shows the chart's tooltip and vertical bar.
 * A separate component, so the hover doesn't rerender the chart.
 */
export const ChartHoverSync = ({
  chartRef,
}: {
  chartRef: RefObject<Chart<"line", ChartDataPoint[], unknown> | null>;
}) => {
  const hoveredTrackpoint = useHoveredTrackpoint();
  const {
    state: { computedTrackpoints },
  } = useRouting();
  const hoveredIndex = hoveredTrackpoint?.index ?? null;
  const trackpointsCount = computedTrackpoints.length;
  // Whether the chart shows a point hovered on the map, so only that is cleared, not the chart's own hover
  const isShowingRef = useRef(false);

  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;

    const meta = chart.getDatasetMeta(0);
    // The chart has a point for every track point, except while the route is being recomputed
    const element =
      hoveredIndex !== null && meta.data.length === trackpointsCount
        ? meta.data[hoveredIndex]
        : undefined;

    if (!element) {
      if (!isShowingRef.current) return;
      isShowingRef.current = false;
      chart.setActiveElements([]);
      chart.tooltip?.setActiveElements([], { x: 0, y: 0 });
      chart.render();
      return;
    }

    const active = [{ datasetIndex: 0, index: hoveredIndex! }];
    isShowingRef.current = true;
    // The point's hover style (the dot) and the tooltip, whose callback updates the tooltip and the bar
    chart.setActiveElements(active);
    chart.tooltip?.setActiveElements(active, { x: element.x, y: element.y });
    chart.render();
  }, [chartRef, hoveredIndex, trackpointsCount]);

  return null;
};
